import Image from 'next/image';
import { LazyHeroVideo } from '@/components/sections/hero/lazy-hero-video';
import { cn } from '@/lib/utils';

interface VideoHeroProps {
  /** When `image`, only the poster/image layer is shown (no video element). */
  variant?: 'video' | 'image';
  /** Optional direct background video file. */
  videoSrc?: string;
  /** Optional secondary source (e.g. webm) for better compression on Safari/Chrome. */
  videoSrcWebm?: string;
  /** Mux playback ID for hosted background video. Takes precedence over local sources. */
  muxPlaybackId?: string;
  /** Poster image — always rendered as the backstop for video and reduced-motion fallback. */
  posterSrc?: string;
  /** Accessible description for the background image when using `variant="image"`. */
  posterAlt?: string;
  /** Section className overrides. */
  className?: string;
  /** Min height; defaults to 100svh. */
  minHeight?: string;
  children?: React.ReactNode;
}

/**
 * Cinematic full-bleed video hero.
 *
 * - Autoplay, muted, looped, playsinline (the only combo browsers permit without a user gesture).
 * - Poster image is rendered behind the video and is *never* removed, so a missing/slow video
 *   never produces a blank frame. A subtle Ken Burns scale keeps the poster alive.
 * - If the video fails to start (e.g. missing file, blocked autoplay), we keep the poster visible
 *   and gently animate it instead of showing a black hole.
 * - Background video plays on all viewports; poster remains as the first-paint backstop.
 */
export function VideoHero({
  variant = 'video',
  muxPlaybackId,
  posterSrc,
  posterAlt = '',
  className,
  minHeight = '100svh',
  children,
}: VideoHeroProps) {
  const imageOnly = variant === 'image';
  const resolvedPosterSrc = muxPlaybackId
    ? `https://image.mux.com/${encodeURIComponent(muxPlaybackId)}/thumbnail.webp?time=0&width=1600`
    : posterSrc;

  return (
    <section
      className={cn(
        'relative isolate flex items-center justify-center overflow-hidden bg-neutral-950 text-white',
        className,
      )}
      style={{ minHeight }}
    >
      {/* Layer 1 — Poster. This is the first-load hero background; video is non-critical. */}
      {resolvedPosterSrc ? (
        <div aria-hidden className="absolute inset-0 -z-20">
          <Image
            src={resolvedPosterSrc}
            alt={imageOnly ? posterAlt : ''}
            fill
            priority
            sizes="100vw"
            className="object-cover object-center"
          />
        </div>
      ) : null}

      {/* Layer 2 — Decorative video. It is never part of the critical render path. */}
      {!imageOnly && muxPlaybackId && resolvedPosterSrc ? (
        <LazyHeroVideo playbackId={muxPlaybackId} />
      ) : null}

      {/* Layer 3 — Cinematic gradient + vignette stack for legibility */}
      <div
        aria-hidden
        className="absolute inset-0 z-1 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.60)_0%,rgba(8,8,10,0.40)_55%,rgba(0,0,0,0.20)_100%)]"
      />
      <div
        aria-hidden
        className="absolute inset-0 z-1 bg-linear-to-t from-neutral-950 via-neutral-950/25 to-transparent"
      />
      {/* Subtle scanline grain so flat frames don't look digital */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-1 opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {children}
    </section>
  );
}
